import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, User, Mail, Save, Loader2, Camera, KeyRound, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import ResetMFAButton from "@/components/ResetMFAButton";
import PasskeyPrompt from "@/components/PasskeyPrompt";

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [showTotpForPasskey, setShowTotpForPasskey] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [verifyingTotp, setVerifyingTotp] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
      return;
    }
    if (user) {
      setEmail(user.email || "");
      loadProfile();
    }
  }, [user, authLoading]);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, date_of_birth, avatar_url")
        .eq("user_id", user!.id)
        .single();

      if (!error && data) {
        setFullName(data.full_name || "");
        setAvatarUrl(data.avatar_url || null);
        if (data.date_of_birth) {
          setDateOfBirth(new Date(data.date_of_birth));
        }
      }
    } catch (err) {
      console.error("Load profile error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: t('common.error'), description: t('profile.imageOnly'), variant: "destructive" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: t('common.error'), description: t('profile.imageTooLarge'), variant: "destructive" });
      return;
    }

    setUploadingAvatar(true);
    try {
      const ext = file.name.split(".").pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      toast({ title: t('profile.uploaded'), description: t('profile.avatarUpdated') });
    } catch (error: any) {
      toast({ title: t('common.error'), description: error.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    const trimmedName = fullName.trim();
    
    // Validate: English letters, numbers, symbols allowed (no Arabic/non-Latin)
    if (trimmedName && !/^[A-Za-z0-9\s\-_'.@#$%&!*+=]+$/.test(trimmedName)) {
      toast({ title: t('common.error'), description: 'الاسم يجب أن يكون بأحرف إنجليزية أو أرقام أو رموز فقط', variant: "destructive" });
      return;
    }

    if (!trimmedName) {
      toast({ title: t('common.error'), description: 'يرجى إدخال الاسم', variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // Check uniqueness
      const { data: existing } = await supabase
        .from("profiles")
        .select("user_id")
        .ilike("full_name", trimmedName)
        .neq("user_id", user!.id)
        .limit(1);

      if (existing && existing.length > 0) {
        toast({ title: t('common.error'), description: 'هذا الاسم مستخدم بالفعل، يرجى اختيار اسم آخر', variant: "destructive" });
        setSaving(false);
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: trimmedName,
          date_of_birth: dateOfBirth ? format(dateOfBirth, "yyyy-MM-dd") : null,
        })
        .eq("user_id", user!.id);

      if (error) throw error;

      toast({ title: t('profile.saved'), description: t('profile.profileUpdated') });
    } catch (error: any) {
      toast({ title: t('common.error'), description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-crypto-purple/10 rounded-full blur-3xl animate-float" style={{ animationDelay: "-3s" }} />
      </div>

      <main className="container mx-auto px-4 pt-24 pb-12 relative z-10 max-w-2xl">
        {/* Avatar + Title */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="relative inline-block mb-4">
            <Avatar className="w-24 h-24 border-4 border-primary/20 shadow-glow">
              <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
              <AvatarFallback className="gradient-primary text-primary-foreground text-2xl font-bold">
                {fullName ? fullName.charAt(0).toUpperCase() : <User className="w-8 h-8" />}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {uploadingAvatar ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarUpload}
            />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t('profile.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('profile.subtitle')}</p>
        </div>

        {/* Personal Info */}
        <div className="glass rounded-2xl p-6 shadow-card animate-scale-in mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-5">{t('profile.personalInfo')}</h2>

          <div className="space-y-5">
            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-foreground">{t('profile.fullName')}</Label>
              <div className="relative">
                <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={fullName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^[A-Za-z0-9\s\-_'.@#$%&!*+=]*$/.test(val)) {
                      setFullName(val);
                    }
                  }}
                  placeholder="Enter your name (English, numbers & symbols)"
                  className="bg-input border-border text-foreground pr-10"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="text-foreground">{t('profile.email')}</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={email}
                  readOnly
                  className="bg-input border-border text-foreground pr-10 opacity-70 cursor-not-allowed"
                  dir="ltr"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div className="space-y-2">
              <Label className="text-foreground">{t('profile.dateOfBirth')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-right font-normal bg-input border-border",
                      !dateOfBirth && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="ml-2 h-4 w-4" />
                    {dateOfBirth
                      ? format(dateOfBirth, "dd/MM/yyyy")
                      : t('profile.selectDateOfBirth')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateOfBirth}
                    onSelect={setDateOfBirth}
                    disabled={(date) =>
                      date > new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                    captionLayout="dropdown-buttons"
                    fromYear={1940}
                    toYear={new Date().getFullYear()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 gradient-primary text-primary-foreground font-semibold"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 ml-2" />
                  {t('profile.saveChanges')}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Security Settings */}
        <div className="glass rounded-2xl p-6 shadow-card animate-scale-in">
          <h2 className="text-lg font-semibold text-foreground mb-5">{t('profile.securitySettings')}</h2>
          <div className="space-y-3">
            <ChangePasswordModal />
            <ResetMFAButton />
            <Button
              variant="outline"
              className="w-full h-12 gap-2 text-base"
              onClick={() => setShowTotpForPasskey(true)}
            >
              <KeyRound className="w-5 h-5" />
              {t('profile.reRegisterPasskey', 'إعادة حفظ مفتاح Passkey')}
            </Button>
          </div>
        </div>

        {/* TOTP Verification Dialog for Passkey */}
        <Dialog open={showTotpForPasskey} onOpenChange={(open) => { if (!open) { setShowTotpForPasskey(false); setTotpCode(""); } }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader className="text-center">
              <div className="flex justify-center mb-3">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
                  <ShieldCheck className="w-7 h-7 text-primary" />
                </div>
              </div>
              <DialogTitle className="text-lg">تأكيد هويتك</DialogTitle>
              <p className="text-sm text-muted-foreground mt-2">
                أدخل رمز المصادقة الثنائية للمتابعة
              </p>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 mt-2">
              <InputOTP
                maxLength={6}
                value={totpCode}
                onChange={setTotpCode}
                autoFocus
              >
                <InputOTPGroup dir="ltr">
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              <Button
                className="w-full h-11 gradient-primary text-primary-foreground font-semibold"
                disabled={totpCode.length !== 6 || verifyingTotp}
                onClick={async () => {
                  setVerifyingTotp(true);
                  try {
                    const { data: factors } = await supabase.auth.mfa.listFactors();
                    const factor = factors?.totp?.find((f) => f.status === "verified");
                    if (!factor) {
                      toast({ title: "لم يتم العثور على عامل MFA", variant: "destructive" });
                      return;
                    }
                    const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
                    if (challengeErr) throw challengeErr;
                    const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: factor.id, challengeId: challenge.id, code: totpCode });
                    if (verifyErr) throw verifyErr;
                    // Success - close TOTP dialog and open Passkey prompt
                    setShowTotpForPasskey(false);
                    setTotpCode("");
                    setShowPasskeyPrompt(true);
                  } catch (err: any) {
                    toast({ title: "رمز غير صحيح", description: err.message, variant: "destructive" });
                  } finally {
                    setVerifyingTotp(false);
                  }
                }}
              >
                {verifyingTotp ? <Loader2 className="w-5 h-5 animate-spin" /> : "تأكيد"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <PasskeyPrompt open={showPasskeyPrompt} onComplete={() => setShowPasskeyPrompt(false)} />
      </main>
    </div>
  );
};

export default Profile;
